import { useEffect, useRef } from "react";

/**
 * useMagneticCursor — atrae sutilmente el elemento hacia el cursor.
 * Máximo desplazamiento: `strength` píxeles. Solo se activa en devices
 * con hover real y pointer fino; en touch no hace nada.
 *
 * Uso:
 *   const ref = useRef(null);
 *   useMagneticCursor(ref, { strength: 10, radius: 90 });
 *   return <button ref={ref}>Hover me</button>;
 */
export function useMagneticCursor(ref, { strength = 12, radius = 100 } = {}) {
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!canHover || reduced) return;

    const el = ref.current;
    if (!el) return;

    let raf = 0;
    let targetX = 0;
    let targetY = 0;
    let curX = 0;
    let curY = 0;
    let active = false;

    const render = () => {
      curX += (targetX - curX) * 0.18;
      curY += (targetY - curY) * 0.18;
      el.style.transform = `translate3d(${curX.toFixed(2)}px, ${curY.toFixed(2)}px, 0)`;
      if (Math.abs(targetX - curX) > 0.05 || Math.abs(targetY - curY) > 0.05 || active) {
        raf = requestAnimationFrame(render);
      } else {
        raf = 0;
        el.style.transform = "";
        el.style.willChange = "";
      }
    };

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist > radius) return;
      const factor = 1 - dist / radius; // 0..1
      targetX = (dx / radius) * strength * factor;
      targetY = (dy / radius) * strength * factor;
      active = true;
      el.style.willChange = "transform";
      if (!raf) raf = requestAnimationFrame(render);
    };

    const onLeave = () => {
      targetX = 0;
      targetY = 0;
      active = false;
      if (!raf) raf = requestAnimationFrame(render);
    };

    // Escuchamos sobre el window (radio de atracción antes de entrar al botón)
    window.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      window.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
      if (raf) cancelAnimationFrame(raf);
      el.style.transform = "";
      el.style.willChange = "";
    };
  }, [ref, strength, radius]);
}

export default useMagneticCursor;
